import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button } from 'antd';
import { ArrowLeft, CircleCheck, KeyRound, Mail, MailCheck } from 'lucide-react';

import { useForgotPassword, useResetPassword, useVerifyResetCode } from '../../../hooks';
import { useI18n } from '../../../i18n';
import { ROUTE_URLS } from '../../../routes';
import { getErrorCode, getErrorMessage, notify } from '../../../utils';
import { AuthLayout } from '../AuthLayout';
import { CODE_LENGTH, CodeInput } from './CodeInput';
import { NewPasswordForm } from './NewPasswordForm';

const RESEND_SECONDS = 60;
const CODE_ERRORS = ['code_required', 'code_invalid', 'code_expired', 'code_attempts_exceeded'];

export const ForgotPassword = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const { isLoading: isSending, mutateAsync: forgotPassword } = useForgotPassword();
  const { isLoading: isVerifying, mutateAsync: verifyCode } = useVerifyResetCode();
  const { isLoading: isResetting, mutateAsync: resetPassword } = useResetPassword();

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendCode = async value => {
    try {
      await forgotPassword({ email: value });
      setEmail(value);
      setCode('');
      setCodeError(null);
      setCooldown(RESEND_SECONDS);
      setStep('code');
    } catch (err) {
      if (step === 'email') {
        form.setFields([{ name: 'email', errors: [getErrorMessage(err)] }]);
      } else {
        notify('error', getErrorMessage(err));
      }
    }
  };

  const showCodeError = err => {
    const errorCode = getErrorCode(err);
    setCodeError(errorCode);
    setCode('');
    if (errorCode === 'code_expired' || errorCode === 'code_attempts_exceeded') setCooldown(0);
  };

  const submitCode = async value => {
    if (value.length !== CODE_LENGTH) {
      setCodeError('code_required');
      return;
    }
    try {
      await verifyCode({ email, code: value });
      setStep('password');
    } catch (err) {
      if (CODE_ERRORS.includes(getErrorCode(err))) {
        showCodeError(err);
      } else {
        notify('error', getErrorMessage(err));
      }
    }
  };

  const submitPassword = async password => {
    try {
      await resetPassword({ email, code, password });
      setStep('done');
    } catch (err) {
      const errorCode = getErrorCode(err);
      if (CODE_ERRORS.includes(errorCode)) {
        showCodeError(err);
        setStep('code');
      } else if (errorCode !== 'password_weak') {
        notify('error', getErrorMessage(err));
      }
      throw err;
    }
  };

  const backLink = (
    <Link to={ROUTE_URLS.LOGIN} className='wm-auth__link wm-auth__back'>
      <ArrowLeft size={16} />
      {t('forgotPassword.backToLogin')}
    </Link>
  );

  if (step === 'done') {
    return (
      <AuthLayout>
        <div className='wm-auth__icon wm-auth__icon--success'>
          <CircleCheck size={24} />
        </div>
        <h1>{t('resetPassword.successTitle')}</h1>
        <p className='wm-muted' style={{ margin: '6px 0 28px' }}>
          {t('resetPassword.successText')}
        </p>
        <Button type='primary' block onClick={() => navigate(ROUTE_URLS.LOGIN)}>
          {t('login.submit')}
        </Button>
      </AuthLayout>
    );
  }

  if (step === 'password') {
    return (
      <AuthLayout>
        <div className='wm-auth__icon'>
          <KeyRound size={24} />
        </div>
        <h1>{t('resetPassword.title')}</h1>
        <p className='wm-muted' style={{ margin: '6px 0 28px' }}>
          {t('resetPassword.subtitle')}
        </p>
        <NewPasswordForm onSubmit={submitPassword} isLoading={isResetting} />
        {backLink}
      </AuthLayout>
    );
  }

  if (step === 'code') {
    return (
      <AuthLayout>
        <div className='wm-auth__icon wm-auth__icon--success'>
          <MailCheck size={24} />
        </div>
        <h1>{t('forgotPassword.codeTitle')}</h1>
        <p className='wm-muted' style={{ margin: '6px 0 4px' }}>
          {t('forgotPassword.codeText')}
        </p>
        <p className='wm-auth__email'>{email}</p>
        <CodeInput
          value={code}
          onChange={value => {
            setCode(value);
            setCodeError(null);
          }}
          onComplete={submitCode}
          error={!!codeError}
          disabled={isVerifying}
          autoFocus
        />
        <p className={`wm-auth__hint ${codeError ? 'wm-auth__error' : 'wm-muted'}`}>
          {codeError ? t(`errors.${codeError}`) : t('forgotPassword.codeHint')}
        </p>
        <Button
          type='primary'
          block
          loading={isVerifying}
          disabled={code.length !== CODE_LENGTH}
          onClick={() => submitCode(code)}
        >
          {t('forgotPassword.verify')}
        </Button>
        <div className='wm-auth__resend'>
          {cooldown > 0 ? (
            <span className='wm-muted'>{t('forgotPassword.resendIn', { count: cooldown })}</span>
          ) : (
            <button
              type='button'
              className='wm-auth__link'
              disabled={isSending}
              onClick={() => sendCode(email)}
            >
              {t('forgotPassword.resend')}
            </button>
          )}
          <span className='wm-muted'>·</span>
          <button type='button' className='wm-auth__link' onClick={() => setStep('email')}>
            {t('forgotPassword.changeEmail')}
          </button>
        </div>
        {backLink}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className='wm-auth__icon'>
        <Mail size={24} />
      </div>
      <h1>{t('forgotPassword.title')}</h1>
      <p className='wm-muted' style={{ margin: '6px 0 28px' }}>
        {t('forgotPassword.subtitle')}
      </p>
      <Form
        form={form}
        name='forgot-password'
        layout='vertical'
        requiredMark={false}
        initialValues={{ email }}
        onFinish={values => sendCode(values.email.trim())}
      >
        <Form.Item
          label={t('forgotPassword.email')}
          name='email'
          rules={[
            { required: true, whitespace: true, message: t('errors.email_required') },
            { type: 'email', message: t('errors.email_invalid') },
          ]}
        >
          <Input
            size='large'
            prefix={<Mail size={16} className='wm-muted' />}
            placeholder={t('forgotPassword.emailPlaceholder')}
            autoComplete='email'
            autoFocus
          />
        </Form.Item>
        <Button type='primary' htmlType='submit' loading={isSending} block style={{ marginTop: 8 }}>
          {t('forgotPassword.submit')}
        </Button>
      </Form>
      {backLink}
    </AuthLayout>
  );
};
